import { apiGet } from './client';
import type { OperationResult, PagedItemsResult, PluginCategory } from './types';

// The plugin-category lookup (ai.PluginCategory) — the logical group a tool
// belongs to (Diabetes, Oncology, Platform, …). A small, slow-changing table
// consumed by three surfaces (catalog, tool detail drawer, agent grant drawer),
// so the promise is memoized: fetched once per session, shared by all callers.
// Call refreshPluginCategories() after editing the vocabulary.

let cache: Promise<PluginCategory[]> | null = null;

async function fetchCategories(): Promise<PluginCategory[]> {
  const wrapped = await apiGet<OperationResult<PagedItemsResult<PluginCategory>>>(
    '/api/PluginCategory/Search',
    { pageNo: 1, pageSize: 200 }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load plugin categories');
  }
  // Server orders by SortOrder already; keep that order.
  return wrapped.data.data ?? [];
}

export function listPluginCategories(): Promise<PluginCategory[]> {
  if (!cache) cache = fetchCategories().catch((e) => { cache = null; throw e; });
  return cache;
}

/** Drop the memoized list so the next read re-fetches (after a vocabulary edit). */
export function refreshPluginCategories(): void {
  cache = null;
}

/** Index a category list by id for O(1) chip lookup on a table row. */
export function indexCategoriesById(cats: PluginCategory[]): Map<number, PluginCategory> {
  return new Map(cats.map((c) => [c.id, c]));
}
